import { signal } from '@preact/signals'
import type { ReadonlySignal, Signal } from '@preact/signals'
import { createContext } from 'preact'
import { useContext } from 'preact/hooks'
import type { CheckoutView } from '#contract'
import { assert } from '#shared'
import type { OsForm } from '../../os-state.ts'
import { createBasket } from './basket.ts'
import type { Basket } from './basket.ts'
import { createNav } from './nav.ts'
import type { ShopNav } from './nav.ts'

/** Why the engine turned an order down. */
export type Refusal = Exclude<CheckoutView['outcome'], 'placed'>

/** One shop's checkout: the order waiting for its answer, or the last refusal. */
interface CheckoutState {
  /** The request sent, and the device it was sent from (its app opens the tracker). */
  readonly pending: { readonly requestId: string; readonly form: OsForm } | null
  /** Shown in the basket until the basket changes or another order goes out. */
  readonly refused: Refusal | null
}

/**
 * The shop apps' state for one level session, shared by the phone and the computer: a
 * basket per shop (so the computer has what the phone put in), a back stack per shop and
 * device, and each shop's checkout. Made next to the OS states, so a reload clears it.
 */
export interface ShopState {
  basket(vendor: string): Basket
  nav(form: OsForm, vendor: string): ShopNav
  checkout(vendor: string): ReadonlySignal<CheckoutState>
  /** Marks an order of `vendor` as sent from `form`; returns the request id to send. */
  begin(vendor: string, form: OsForm): string
  /** Applies the engine's answer to the shop that asked; an answer nobody waits for is ignored. */
  settle(outcome: CheckoutView): void
}

/** An unanswered order stops waiting after this long (it was sent while not playing). */
const PENDING_MS = 2000
const IDLE: CheckoutState = { pending: null, refused: null }

export function createShopState(): ShopState {
  const baskets = new Map<string, Basket>()
  const navs = new Map<string, ShopNav>()
  const checkouts = new Map<string, Signal<CheckoutState>>()
  let requests = 0

  const checkout = (vendor: string): Signal<CheckoutState> => {
    let state = checkouts.get(vendor)
    if (!state) {
      state = signal(IDLE)
      checkouts.set(vendor, state)
    }
    return state
  }
  const basket = (vendor: string): Basket => {
    let made = baskets.get(vendor)
    if (!made) {
      const created = createBasket()
      // A changed basket is a new question: the old refusal no longer applies.
      created.counts.subscribe(() => {
        const state = checkout(vendor)
        if (state.peek().refused) state.value = { ...state.peek(), refused: null }
      })
      baskets.set(vendor, created)
      made = created
    }
    return made
  }
  const nav = (form: OsForm, vendor: string): ShopNav => {
    const key = `${form}:${vendor}`
    let made = navs.get(key)
    if (!made) {
      made = createNav()
      navs.set(key, made)
    }
    return made
  }

  return {
    basket,
    nav,
    checkout,
    begin(vendor, form) {
      requests += 1
      const requestId = `${vendor}.${Date.now().toString(36)}.${String(requests)}`
      const state = checkout(vendor)
      state.value = { pending: { requestId, form }, refused: null }
      setTimeout(() => {
        if (state.value.pending?.requestId === requestId) state.value = IDLE
      }, PENDING_MS)
      return requestId
    },
    settle(outcome) {
      for (const [vendor, state] of checkouts) {
        const { pending } = state.value
        if (pending?.requestId !== outcome.requestId) continue
        if (outcome.outcome === 'placed') {
          state.value = IDLE
          basket(vendor).clear()
          const { orderId } = outcome
          nav(pending.form, vendor).reset(
            orderId === undefined ? undefined : { kind: 'order', order: orderId },
          )
        } else {
          state.value = { pending: null, refused: outcome.outcome }
        }
        return
      }
    },
  }
}

export const ShopStateContext = createContext<ShopState | null>(null)

export function useShopState(): ShopState {
  const state = useContext(ShopStateContext)
  assert(state, 'useShopState() needs <Devices> above it')
  return state
}
